/**
 * The conversations column: a search box, then the conversations grouped by last activity
 * (Pinned first). Each row is one line: what is going on (working, needs approval, failed), a
 * draft marker, the name, and when; its menu renames, pins, downloads or deletes. Rename
 * happens in place: Enter or leaving the field saves, Esc cancels.
 */
import { Check, Download, MessageSquare, MessagesSquare, MoreHorizontal, Pencil, Pin, PinOff, Search, Trash2, X } from '@orgus/ui/icons';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ConversationSummary } from '@orgus/protocol';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Input,
  Pressable,
  RelativeTime,
  SkeletonRows,
  WorkStateIcon,
  cn,
  motionPresets,
} from '@orgus/ui';
import { PIN_LIMIT, groupConversations, searchConversations } from './listing.ts';

export interface ConversationListActions {
  onOpen: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onPin: (id: string, pinned: boolean) => Promise<void>;
  onDownload: (id: string) => void;
  onDelete: (id: string) => void;
}

function StateMark({ c }: { c: ConversationSummary }) {
  if (c.state === 'running') return <WorkStateIcon state="running" />;
  if (c.state === 'waiting' || c.pending > 0) return <WorkStateIcon state="needs-you" />;
  if (c.state === 'failed') return <WorkStateIcon state="failed" />;
  return <MessageSquare className="size-4 shrink-0 text-fg-faint" aria-hidden />;
}

const STATE_WORD: Partial<Record<ConversationSummary['state'], string>> = { running: 'working', waiting: 'needs you', failed: 'failed' };

function RenameField({ title, onSave, onCancel }: { title: string; onSave: (t: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(title);
  const done = useRef(false);
  const save = () => {
    if (done.current) return;
    done.current = true;
    if (v.trim() && v.trim() !== title) onSave(v.trim());
    else onCancel();
  };
  return (
    <div className="flex h-row items-center gap-1 px-2">
      <Input
        aria-label="Conversation name"
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            done.current = true;
            onCancel();
          }
        }}
        maxLength={120}
        wrapperClassName="min-w-0 flex-1"
      />
      <IconButton size="xs" label="Save this name" icon={<Check />} onMouseDown={(e) => e.preventDefault()} onClick={save} />
      <IconButton
        size="xs"
        label="Cancel renaming"
        icon={<X />}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          done.current = true;
          onCancel();
        }}
      />
    </div>
  );
}

function Row({ c, selected, draft, pinFull, actions, renaming, setRenaming }: { c: ConversationSummary; selected: boolean; draft: boolean; pinFull: boolean; actions: ConversationListActions; renaming: boolean; setRenaming: (on: boolean) => void }) {
  if (renaming) {
    return (
      <li>
        <RenameField
          title={c.title}
          onCancel={() => setRenaming(false)}
          onSave={(t) => {
            setRenaming(false);
            void actions.onRename(c.id, t);
          }}
        />
      </li>
    );
  }
  return (
    <li data-row className={cn('group relative flex h-row items-center rounded-control', !selected && 'hover:bg-hover')}>
      {selected && (
        <motion.span layoutId="assistant-conversation" transition={motionPresets.move} className="selected-tint absolute inset-0 rounded-control" aria-hidden />
      )}
      <Pressable
        onClick={() => actions.onOpen(c.id)}
        aria-current={selected || undefined}
        className="relative flex h-full min-w-0 flex-1 items-center gap-2 pl-3 pr-1 text-left"
        title={c.title}
      >
        <StateMark c={c} />
        {draft && !selected && <span className="shrink-0 text-caption font-medium text-fg-subtle">Draft</span>}
        <span className={cn('min-w-0 flex-1 truncate text-ui', selected ? 'font-medium text-fg' : 'text-fg-muted')}>
          {c.title}
          {(STATE_WORD[c.state] || c.pending > 0) && <span className="sr-only">, {STATE_WORD[c.state] ?? 'needs you'}</span>}
        </span>
        <span className="shrink-0 text-caption text-fg-subtle num group-focus-within:hidden group-hover:hidden" data-meta>
          <RelativeTime ts={c.updatedAt} />
        </span>
      </Pressable>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton size="xs" label={`Actions for ${c.title}`} icon={<MoreHorizontal />} className="relative mr-1 hidden shrink-0 group-focus-within:flex group-hover:flex data-[state=open]:flex" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem icon={<Pencil />} onSelect={() => setRenaming(true)}>
            Rename
          </DropdownMenuItem>
          {c.pinned ? (
            <DropdownMenuItem icon={<PinOff />} onSelect={() => void actions.onPin(c.id, false)}>
              Unpin
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem icon={<Pin />} disabled={pinFull} onSelect={() => void actions.onPin(c.id, true)}>
              {pinFull ? `Pin (up to ${PIN_LIMIT})` : 'Pin'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem icon={<Download />} onSelect={() => actions.onDownload(c.id)}>
            Download transcript
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem icon={<Trash2 />} variant="destructive" onSelect={() => actions.onDelete(c.id)}>
            Delete conversation
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

export function ConversationList({
  conversations,
  selected,
  drafts,
  actions,
  loading,
  searchRef,
  now = Date.now(),
}: {
  conversations: ConversationSummary[];
  selected: string | null;
  drafts: ReadonlySet<string>;
  actions: ConversationListActions;
  loading?: boolean;
  searchRef?: React.Ref<HTMLInputElement>;
  now?: number;
}) {
  const [query, setQuery] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const found = useMemo(() => searchConversations(conversations, query), [conversations, query]);
  const groups = useMemo(() => groupConversations(found, now), [found, now]);
  const pinFull = conversations.filter((c) => c.pinned).length >= PIN_LIMIT;
  useEffect(() => {
    if (renaming && !conversations.some((c) => c.id === renaming)) setRenaming(null);
  }, [conversations, renaming]);

  if (loading) return <SkeletonRows rows={6} />;
  return (
    <div className="flex flex-col gap-2">
      <div className="px-3 pb-1">
        <Input
          ref={searchRef}
          aria-label="Search conversations"
          placeholder="Search conversations"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && query && (e.stopPropagation(), setQuery(''))}
          icon={<Search />}
        />
      </div>
      {!conversations.length ? (
        <EmptyState icon={<MessagesSquare />} title="No conversations yet" description="Ask the assistant something to start one." />
      ) : !found.length ? (
        <p className="px-4 py-2 text-ui text-fg-muted">No conversation matches “{query.trim()}”.</p>
      ) : (
        groups.map((g) => (
          <section key={g.key} aria-label={g.label} className="flex flex-col px-2 motion-arrive">
            <h3 className="flex h-8 items-center px-3 text-caption font-medium text-fg-subtle">{g.label}</h3>
            <ul className="flex flex-col" data-list>
              {g.items.map((c) => (
                <Row
                  key={c.id}
                  c={c}
                  selected={c.id === selected}
                  draft={drafts.has(c.id)}
                  pinFull={pinFull}
                  actions={actions}
                  renaming={renaming === c.id}
                  setRenaming={(on) => setRenaming(on ? c.id : null)}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
